import { memo } from 'react'
import { getTextareaClasses } from '../../styles/inputStyles'
import { formatTimestamp } from '../../utils/timeFormatters'
import { useTranslation } from 'react-i18next'
import MessageActions from './MessageActions'
import GlassmorphButton from '../ui/GlassmorphButton'

/**
 * MessageList - Displays a list of channel messages with avatars and timestamps
 */
const MessageList = memo(function MessageList({ 
  messages, 
  messagesEndRef, 
  brokenAvatars, 
  onAvatarError,
  currentUserId,
  isAdmin,
  onEditMessage,
  onDeleteMessage,
  editingMessage,
  editContent,
  onEditContentChange,
  onSaveEdit,
  onCancelEdit,
  hasMoreMessages,
  loadingOlderMessages,
  onLoadMore
}) {
  const { t } = useTranslation()

  return (
    <div className="py-3 px-3 space-y-3">
      {/* Load More Button */}
      {hasMoreMessages && (
        <div className="flex justify-center pb-2">
          <button
            onClick={onLoadMore}
            onTouchEnd={(e) => { e.preventDefault(); if (!loadingOlderMessages) onLoadMore() }}
            disabled={loadingOlderMessages}
            className="px-4 py-2 text-sm font-medium text-white/80 hover:text-white bg-white/5 hover:bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 hover:border-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loadingOlderMessages ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {t('channels.message.loadingMore')}
              </>
            ) : t('channels.message.loadMore')}
          </button>
        </div>
      )}

      {messages.length === 0 && !hasMoreMessages && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <svg className="h-10 w-10 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p className="text-sm text-gray-500">{t('channels.noMessages', 'No messages yet. Start the conversation!')}</p>
        </div>
      )}
      
      {messages.map((msg) => (
        <MessageItem
          key={msg.id}
          message={msg}
          brokenAvatars={brokenAvatars}
          onAvatarError={onAvatarError}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          onEditMessage={onEditMessage}
          onDeleteMessage={onDeleteMessage}
          isEditing={editingMessage?.id === msg.id}
          editContent={editContent}
          onEditContentChange={onEditContentChange}
          onSaveEdit={onSaveEdit}
          onCancelEdit={onCancelEdit}
          t={t}
        />
      ))}
      <div ref={messagesEndRef} />
    </div>
  )
})

/**
 * Individual message item
 */
const MessageItem = memo(function MessageItem({ 
  message, 
  brokenAvatars, 
  onAvatarError, 
  currentUserId,
  isAdmin,
  onEditMessage,
  onDeleteMessage,
  isEditing,
  editContent,
  onEditContentChange,
  onSaveEdit,
  onCancelEdit,
  t 
}) {
  // System messages get special styling (centered, italic, no avatar)
  if (message.isSystemMessage) {
    return (
      <div className="flex justify-center py-2">
        <div className="flex items-center gap-2 text-xs text-gray-500 italic">
          <span>{message.content}</span>
          <span className="text-gray-400">·</span>
          <span className="text-gray-400">{formatTimestamp(message.createdAt, t)}</span>
        </div>
      </div>
    )
  }

  const showAvatar = message.userAvatar && !brokenAvatars.has(message.userAvatar)
  const initials = message.userName
    ? message.userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?'

  return (
    <div className="flex gap-2 group">
      {showAvatar ? (
        <img 
          src={message.userAvatar} 
          alt="" 
          className="w-8 h-8 rounded-full object-cover flex-shrink-0" 
          onError={() => onAvatarError(message.userAvatar)}
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center bg-primary/20 border border-primary/40">
          <span className="text-primary text-xs font-semibold">{initials}</span>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-gray-900">{message.userName}</span>
          <span className="text-xs text-gray-500">{formatTimestamp(message.createdAt, t)}</span>
          {message.isEdited && !isEditing && (
            <span className="text-xs text-gray-400 italic">({t('channels.message.edited')})</span>
          )}
          {currentUserId && (
            <MessageActions
              message={message}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              onEdit={onEditMessage}
              onDelete={onDeleteMessage}
              isEditing={isEditing}
            />
          )}
        </div>
        
        {isEditing ? (
          <div className="mt-2 space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => onEditContentChange(e.target.value)}
              className={getTextareaClasses()}
              rows={3}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => onSaveEdit(message.id)}
                disabled={!editContent.trim() || editContent.trim().length < 10}
                className="px-3 py-1 bg-primary text-white text-sm rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('channels.message.save')}
              </button>
              <button
                onClick={onCancelEdit}
                className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
              >
                {t('channels.message.cancel')}
              </button>
              {editContent.trim().length < 10 && editContent.trim().length > 0 && (
                <span className="text-xs text-gray-500 self-center">
                  {t('channels.message.charactersRemaining', { count: 10 - editContent.trim().length })}
                </span>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-700 mt-1 break-words">{message.content}</p>
        )}
      </div>
    </div>
  )
})

export default MessageList
